import _ from "lodash";
import { IRowData, IShipment } from "components/shipment-review/types";
import { ILineItem } from "constants/order-types";
import { EventHandler } from "react";
import { AllocationType } from "types/cart";
import { addLineItem, IWorkingUpdate, updateSize } from "actions/cart-legacy";
import { SchemaField, SchemaFieldSubtype } from "types/schema";
import { IGridEntry, LineItemAttributes } from "types/cart/shipment-review";
import me from "models/me";
import { Iterable } from "immutable";
import transit from "transit-immutable-js";
import { IGridSettings } from "reducers/cart-grid-view";
import type { IProductSize } from "types/product";
import { getQuantityWithUnitsPerPack } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { isAllocated } from "helpers/legacy-cart/bulk";

export const cancelEvent: EventHandler<any> = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

export const mapSize = (size: IProductSize) => _.pick(size, ["size", "size_group"]);

export const mapLineItem = (row: IRowData): Partial<ILineItem> => ({
    prebook: row.prebook,
    warehouse: row.warehouse?.id,
    // rewrite other attributes to avoid coping value from initial line item
    ...mapAttributes(row.lineItemAttributes),
    ...getRowRange(row.splitFields),
});

export const getRowRange = ({ shipStart, shipEnd }: IShipment): Pick<ILineItem, "ship_start" | "ship_end"> => ({
    ship_start: shipStart.toISOString(),
    ship_end: shipEnd.toISOString(),
});

export const getWOUpdateSizeAction = (
    item: IGridEntry,
    size: IProductSize,
    quantity: number,
    overrides?: { [key: string]: any }
): IWorkingUpdate => {
    const update = {
        ...mapSize(size),
        quantity,
        warehouse: item.warehouse?.id,
        location: item.location?.id,
        ...(overrides ? overrides : {}),
    };
    // set allocated key when creating a new item from virtual
    const allocation = item.location && {
        allocated: item.location?.id ? AllocationType.ByLocation : AllocationType.Combined,
    };
    // TODO GV: update to move virtual check to WO
    if (item.cartLineItemId) {
        return updateSize(item.cartLineItemId, update);
    }
    const lineItem = {
        prebook: item.prebook,
        warehouse: item.warehouse?.id,
        // rewrite other attributes to avoid coping value from initial line item
        ...mapAttributes(item.attributes),
        ...getRowRange(item),
    };
    // TODO temporary solution. mapId should be removed
    return addLineItem(mapId(item.id), { ...lineItem, ...allocation, sizes: [update] });
};

// right now support for sizing grid only single string and lookup fields
export const getSupportedFDMFields = (fdm: SchemaField[]) =>
    _.filter(
        getLineItemsFDMFields(fdm),
        ({ details: { subtype, condition } }: SchemaField) =>
            [SchemaFieldSubtype.String, SchemaFieldSubtype.Lookup].includes(subtype) && _.isEmpty(condition)
    );

export const shouldExposeFDMField = (field: SchemaField) =>
    me.account_type === "buyer" ? field.display_to_buyers : field.display;

export const getLineItemsFDMFields = (fdm: SchemaField[]) =>
    _.filter(fdm, (field) => field.key.startsWith("line_items.") && shouldExposeFDMField(field));

export const isFDMSupports = (field: SchemaField): boolean =>
    [SchemaFieldSubtype.String, SchemaFieldSubtype.Lookup].includes(field.details.subtype) &&
    _.isEmpty(field.details.condition) &&
    field.key.startsWith("line_items.") &&
    !!shouldExposeFDMField(field);

export const mapLineItemFDMField = ({ key }: SchemaField) => key.replace(/^line_items\./i, "");

export const mapAttributes = (attributes: LineItemAttributes) =>
    _.chain(attributes)
        .mapKeys(({ field }) => field)
        .mapValues(({ value }) => value)
        .value();

export const transitGridSettings = (gridSettings: IGridSettings = {}, type: "pack" | "unpack") => {
    const packer =
        type === "pack"
            ? (value) => (Iterable.isIterable(value) ? transit.toJSON(value) : value)
            : (value) => transit.fromJSON(value as any);

    return {
        ...gridSettings,
        ..._.mapValues(_.pick(gridSettings, ["rowIds", "selectedRows", "expandedSizes"]), packer),
    };
};

/**
 * Maps SizingGridLineItem ref to WO LineItem _id.
 */
export const mapId = (ref) => ref?.split("_")?.[0];

export enum ItemTotalType {
    Units = "units",
    Wholesale = "wholesale",
    Retail = "retail",
}
export const getTotalsFromEntries = (entries: IGridEntry[]): { [key in ItemTotalType]: number } =>
    _.reduce(
        entries,
        (totals, entry) => {
            const hideEntryPrice = !isAllocated(entry.allocated, entry.location);
            const wholesalePrice = hideEntryPrice ? 0 : (entry.pricing?.discounted_price ?? entry.pricing?.wholesale);
            const retailPrice = hideEntryPrice ? 0 : entry.pricing?.retail;
            const quantityWithPacks = getQuantityWithUnitsPerPack(entry.quantity, entry.unitsPerPack);
            totals[ItemTotalType.Units] += quantityWithPacks;
            totals[ItemTotalType.Wholesale] += quantityWithPacks * wholesalePrice;
            totals[ItemTotalType.Retail] += quantityWithPacks * retailPrice;
            return totals;
        },
        { [ItemTotalType.Units]: 0, [ItemTotalType.Retail]: 0, [ItemTotalType.Wholesale]: 0 }
    );
